/**
 * A budget month, as `YYYY-MM`. Budget months are calendar months in the user's
 * own timezone and are never derived from a UTC instant — booking a 31 July
 * 11pm NZDT purchase into the August envelope would be wrong.
 */

/** String-literal brand, for the same reason as `Milliunits` — see money.ts. */
export type BudgetMonth = string & { readonly __brand: 'BudgetMonth' }

const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/

export function budgetMonth(value: string): BudgetMonth {
  if (!MONTH_PATTERN.test(value)) {
    throw new RangeError(`Invalid budget month "${value}", expected YYYY-MM`)
  }
  return value as BudgetMonth
}

export function isBudgetMonth(value: string): value is BudgetMonth {
  return MONTH_PATTERN.test(value)
}

export function fromParts(year: number, month1to12: number): BudgetMonth {
  if (!Number.isInteger(year) || year < 1000 || year > 9999) {
    throw new RangeError(`Year out of range: ${year}`)
  }
  if (!Number.isInteger(month1to12) || month1to12 < 1 || month1to12 > 12) {
    throw new RangeError(`Month out of range: ${month1to12}`)
  }
  return `${year}-${String(month1to12).padStart(2, '0')}` as BudgetMonth
}

export function toParts(month: BudgetMonth): { year: number; month: number } {
  const match = MONTH_PATTERN.exec(month)
  if (!match) throw new RangeError(`Invalid budget month "${month}"`)
  return { year: Number(match[1]), month: Number(match[2]) }
}

/** The budget month containing an ISO date string (`YYYY-MM-DD`). */
export function fromISODate(isoDate: string): BudgetMonth {
  const match = /^(\d{4})-(\d{2})-\d{2}/.exec(isoDate)
  if (!match) throw new RangeError(`Invalid ISO date "${isoDate}"`)
  return budgetMonth(`${match[1]}-${match[2]}`)
}

export function addMonths(month: BudgetMonth, delta: number): BudgetMonth {
  const { year, month: m } = toParts(month)
  const zeroBased = year * 12 + (m - 1) + delta
  return fromParts(Math.floor(zeroBased / 12), (zeroBased % 12) + 1)
}

export function next(month: BudgetMonth): BudgetMonth {
  return addMonths(month, 1)
}

export function previous(month: BudgetMonth): BudgetMonth {
  return addMonths(month, -1)
}

/** Signed count of months from `a` to `b`. `diff('2026-01','2026-03') === 2`. */
export function diff(a: BudgetMonth, b: BudgetMonth): number {
  const pa = toParts(a)
  const pb = toParts(b)
  return (pb.year - pa.year) * 12 + (pb.month - pa.month)
}

export function compare(a: BudgetMonth, b: BudgetMonth): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** Inclusive range, ascending. */
export function range(from: BudgetMonth, to: BudgetMonth): BudgetMonth[] {
  const span = diff(from, to)
  if (span < 0) return []
  return Array.from({ length: span + 1 }, (_, i) => addMonths(from, i))
}

/** First and last calendar day of the month, as ISO date strings. */
export function bounds(month: BudgetMonth): { start: string; endExclusive: string } {
  return { start: `${month}-01`, endExclusive: `${next(month)}-01` }
}

export function daysInMonth(month: BudgetMonth): number {
  const { year, month: m } = toParts(month)
  return new Date(Date.UTC(year, m, 0)).getUTCDate()
}

/** Sunday is 0, matching `Date.getUTCDay` and YNAB's `goal_day` for a weekly target. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6

/**
 * How many times `weekday` falls in `month` — four, or five.
 *
 * Not a flat four. "$50 a week" in a five-week month is $250, and YNAB counts the
 * actual weekday when it asks for a weekly target, so a figure derived from the target
 * has to count the same way or it disagrees with the screen it came from.
 */
export function weeksInMonth(month: BudgetMonth, weekday: Weekday): number {
  const { year, month: monthOfYear } = toParts(month)
  const firstOfMonth = new Date(Date.UTC(year, monthOfYear - 1, 1)).getUTCDay()
  // Days from the 1st to the weekday's first occurrence, then every 7th after.
  const firstOccurrence = (weekday - firstOfMonth + 7) % 7
  return Math.floor((daysInMonth(month) - firstOccurrence - 1) / 7) + 1
}

/**
 * Today as a calendar date in the user's timezone, `YYYY-MM-DD`.
 *
 * Not `new Date().toISOString().slice(0, 10)`, which is today in UTC — and for
 * thirteen hours of an Auckland day that is yesterday. Anything comparing against a
 * stored date has to ask in the same timezone those dates were written in.
 *
 * `en-CA` because its short date format is already `YYYY-MM-DD`, the same trick
 * `currentMonth` below uses.
 */
export function today(timeZone = 'Pacific/Auckland', now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

export function currentMonth(timeZone = 'Pacific/Auckland', now: Date = new Date()): BudgetMonth {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now)

  const year = parts.find((p) => p.type === 'year')?.value
  const month = parts.find((p) => p.type === 'month')?.value
  if (!year || !month) throw new Error(`Could not resolve current month for ${timeZone}`)
  return budgetMonth(`${year}-${month}`)
}

export function formatLong(month: BudgetMonth, locale = 'en-NZ'): string {
  const { year, month: m } = toParts(month)
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, m - 1, 1)))
}
